import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    subject: 'general'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Contact form submitted:', formData);
    // You would typically send this to your backend
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">MockupPro</h1>
          </div>
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Pricing
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Login
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* Contact Info */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Have questions about MockupPro? We're here to help! Reach out to our team 
              and we'll get back to you within 24 hours.
            </p>

            <div className="space-y-8">
              
              {/* Sales */}
              <div className="flex items-start">
                <div className="bg-blue-100 rounded-lg p-3 mr-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sales & Pricing</h3>
                  <p className="text-gray-600 mb-2">
                    Questions about our plans, custom pricing, or need a demo?
                  </p>
                  <a href="mailto:sales@mockuppro.com" className="text-blue-600 hover:text-blue-800">
                    sales@mockuppro.com
                  </a>
                </div>
              </div>

              {/* Support */}
              <div className="flex items-start">
                <div className="bg-green-100 rounded-lg p-3 mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 11-9.75 9.75 9.75 9.75 0 019.75-9.75z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Technical Support</h3>
                  <p className="text-gray-600 mb-2">
                    Need help with your account, billing, or technical issues?
                  </p>
                  <a href="mailto:support@mockuppro.com" className="text-blue-600 hover:text-blue-800">
                    support@mockuppro.com
                  </a>
                </div>
              </div>

              {/* Partnerships */}
              <div className="flex items-start">
                <div className="bg-purple-100 rounded-lg p-3 mr-4">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Partnerships</h3>
                  <p className="text-gray-600 mb-2">
                    Interested in partnering with us or becoming an affiliate?
                  </p>
                  <a href="mailto:partners@mockuppro.com" className="text-blue-600 hover:text-blue-800">
                    partners@mockuppro.com
                  </a>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Times</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sales inquiries:</span>
                    <span className="font-medium text-gray-900">Within 2 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Technical support:</span>
                    <span className="font-medium text-gray-900">Within 24 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">General questions:</span>
                    <span className="font-medium text-gray-900">Within 48 hours</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your company name (optional)"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="general">General Question</option>
                  <option value="sales">Sales & Pricing</option>
                  <option value="support">Technical Support</option>
                  <option value="billing">Billing & Account</option>
                  <option value="partnership">Partnership Inquiry</option>
                  <option value="feature">Feature Request</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors transform hover:scale-105"
              >
                Send Message
              </button>

              <p className="text-sm text-gray-600 text-center">
                We'll get back to you within 24 hours. For urgent issues, please email 
                <a href="mailto:support@mockuppro.com" className="text-blue-600 hover:text-blue-800 ml-1">
                  support@mockuppro.com
                </a>
              </p>
            </form>
          </div>

        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Quick Answers
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">How do I upgrade my plan?</h4>
                <p className="text-gray-600">
                  You can upgrade your plan anytime from your dashboard settings. Changes take effect immediately.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Do you offer custom integrations?</h4>
                <p className="text-gray-600">
                  Yes! Our Enterprise plan includes custom integrations. Contact our sales team for more details.
                </p>
              </div>
              <div>
                <h4 className="font-semibent text-gray-900 mb-2">Can I get a demo?</h4>
                <p className="text-gray-600">
                  Absolutely! We offer personalized demos for Agency and Enterprise plans. Just reach out to our sales team.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Is my data secure?</h4>
                <p className="text-gray-600">
                  Yes, we use enterprise-grade security with encryption at rest and in transit. SOC 2 Type II compliant.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Do you have an API?</h4>
                <p className="text-gray-600">
                  Yes! Our API is available with Agency and Enterprise plans. Full documentation available in our developer portal.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Can I white-label MockupPro?</h4>
                <p className="text-gray-600">
                  Yes, white-labeling is available with Agency and Enterprise plans. Remove our branding and add your own.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h3 className="text-3xl font-bold text-gray-900 mb-4">
          Still have questions?
        </h3>
        <p className="text-xl text-gray-600 mb-8">
          Our team is here to help you succeed with MockupPro
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => navigate('/signup')}
            className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors transform hover:scale-105"
          >
            Start Free Trial
          </button>
          <button 
            onClick={() => window.location.href = 'mailto:sales@mockuppro.com'}
            className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors"
          >
            Talk to Sales
          </button>
        </div>
      </div>
    </div>
  );
};

export default Contact;